import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { TruncatedText } from "@/components/ui/truncated-text";

interface KeywordPickerProps {
  keywords: string[];
  kpsScores?: Record<string, number>;
  onSelect: (kw: string) => void;
  integrationSlot?: React.ReactNode;
  selectedKeyword?: string | null;
  onResetKeyword?: () => void;
}

export function KeywordPicker({
  keywords,
  kpsScores,
  onSelect,
  integrationSlot,
  selectedKeyword,
  onResetKeyword,
}: KeywordPickerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const getKps = useCallback((kw: string) => kpsScores?.[kw.toLowerCase()], [kpsScores]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const unique = Array.from(new Set(keywords));
    const isExact = q.startsWith('"') && q.endsWith('"') && q.length > 2;
    const term = isExact ? q.slice(1, -1) : q;
    const matched = term
      ? unique.filter((k) => isExact ? k.toLowerCase() === term : k.toLowerCase().includes(term))
      : unique;
    if (kpsScores) {
      matched.sort((a, b) => (getKps(b) ?? -1) - (getKps(a) ?? -1));
    }
    return matched.slice(0, 50);
  }, [keywords, query, kpsScores, getKps]);

  const showList = open && (query.trim().length > 0 || filtered.length > 0);

  return (
    <section className="space-y-2.5 border-t border-border pt-6">
      <h5 className="text-base font-semibold leading-6 text-foreground">Keywords</h5>
      <p className="text-xs text-muted-foreground">
        Search or click to see a list of the master keywords for this product. Once a keyword is selected, AI will make suggestions for how to integrate it into the phrasing.
      </p>
      {selectedKeyword ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="min-w-0 flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Selected:</span>
            <TruncatedText className="text-sm font-medium text-foreground">{selectedKeyword}</TruncatedText>
          </div>
          {onResetKeyword && (
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={onResetKeyword}>
              Reset
            </Button>
          )}
        </div>
      ) : (
        <div className="relative">
          <ClearableInput
            value={query}
            onChange={(value) => { setQuery(value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => { setTimeout(() => setOpen(false), 120); }}
            placeholder='Search keywords… (use "quotes" for exact match)'
            className="h-8 text-sm"
            icon={null}
          />
          {showList && (
            <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-md border border-border bg-card shadow-md">
              {filtered.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic px-2.5 py-2">No matches.</p>
              ) : (
                <ul className="max-h-44 overflow-y-auto divide-y divide-border">
                  {filtered.map((kw) => {
                    const kps = getKps(kw);
                    return (
                      <li key={kw}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { onSelect(kw); setOpen(false); setQuery(""); }}
                          className="w-full flex items-center justify-between gap-3 px-2.5 py-1.5 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                        >
                          {/* eslint-disable-next-line signal/no-unwrapped-clipped-text -- inside-an-open-overlay */}
                          <span className="truncate text-left">{kw}</span>
                          {typeof kps === "number" && (
                            <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                              KPS {kps}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
      {integrationSlot}
    </section>
  );
}
